import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { User } from "oidc-client";
//import { userManager } from "./authService";
import { userManager } from '../../utils/services/oidc.service';
import { IDENTITY_CONFIG } from '../../config/oidc.config';

import { Box, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const toExpiresAt = (expiresIn: number): number => Math.floor(Date.now() / 1000) + expiresIn;

const parseTokensFromQuery = (search: string): any => {
  const params = new URLSearchParams(search);

  const access_token  = params.get("access_token");
  const refresh_token = params.get("refresh_token");
  const id_token      = params.get("id_token");
  const expires_in    = params.get("expires_in");

  if (!access_token)  throw new Error("Missing query param: access_token");
  if (!refresh_token) throw new Error("Missing query param: refresh_token");
  if (!id_token)      throw new Error("Missing query param: id_token");
  if (!expires_in)    throw new Error("Missing query param: expires_in");

  const expiresInNum = Number(expires_in);
  if (isNaN(expiresInNum) || expiresInNum <= 0)
    throw new Error("Invalid expires_in value");

  return { access_token, refresh_token, id_token, expires_in: expiresInNum };
};

type Status = "loading" | "success" | "error";

const AutoLoginRedirect = () => {
  const navigate  = useNavigate();
  const location  = useLocation();               // gives us location.search
  const [status, setStatus] = useState<Status>("loading");
  const [error,  setError]  = useState<string | null>(null);

  useEffect(() => {
    const autoLogin = async () => {
      try {
        // 1. Parse tokens from URL query params
        const { access_token, refresh_token, id_token, expires_in } =
          parseTokensFromQuery(location.search);

        // 2. Derive expires_at from expires_in
        const expires_at = toExpiresAt(expires_in);

        // 3. Decode id_token for profile claims
        //const profile = decodeJwt(id_token);

        // 4. Derive scope from profile or fallback
        const scope: string = IDENTITY_CONFIG['scope'] ?? "openid profile email offline_access";

        // 5. Build and store User
        const user = new User({
          access_token,
          refresh_token,
          id_token,
          expires_at,           // ← computed: now + expires_in
          token_type: "Bearer",
          scope,
          session_state: '',
          profile: {} as any,
          state: null,
        });

        await userManager.storeUser(user);

        setStatus("success");
        setTimeout(() => navigate("/", { replace: true }), 800);
      } catch (err: any) {
        setError(err?.message ?? "Auto login failed");
        setStatus("error");
      }
    };

    autoLogin();
  }, [navigate, location.search]);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="grey.100"
    >
      <Card elevation={3} sx={{ borderRadius: 3, minWidth: 320 }}>
        <CardContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
            py={3}
            px={4}
          >
            {status === "loading" && (
              <>
                <CircularProgress size={48} thickness={4} />
                <Typography variant="body1" color="text.secondary">
                  Signing you in...
                </Typography>
              </>
            )}

            {status === "success" && (
              <>
                <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "success.main" }} />
                <Typography variant="body1" color="text.secondary">
                  Authenticated! Redirecting...
                </Typography>
              </>
            )}

            {status === "error" && (
              <>
                <ErrorOutlineIcon sx={{ fontSize: 48, color: "error.main" }} />
                <Typography variant="body1" color="text.secondary">
                  Login failed
                </Typography>
                {error && (
                  <Typography variant="caption" color="error.main" textAlign="center">
                    {error}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AutoLoginRedirect;